import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Phone, Loader2, Eye, EyeOff, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [twoFaRequired, setTwoFaRequired] = useState(false);
  const [twoFaMethod, setTwoFaMethod] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  // Handle impersonation token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const impToken = params.get('impersonate_token');
    if (impToken) {
      login(null, null, impToken);
      navigate('/');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Errore di login');

      if (data.two_factor_required) {
        setTwoFaRequired(true);
        setTwoFaMethod(data.two_factor_method);
        setLoading(false);
        return;
      }

      // Normal login
      localStorage.setItem('access_token', data.access_token);
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/2fa/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: twoFaCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Codice non valido');

      localStorage.setItem('access_token', data.access_token);
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-sky-500 rounded-2xl mb-4">
            <Phone className="w-8 h-8 text-white" />
          </div>
          <h1 
            className="text-3xl font-bold text-slate-800"
            style={{ fontFamily: 'Manrope' }}
          >
            VoiceBook
          </h1>
          <p className="text-slate-500 mt-1">Sistema Prenotazioni Centro Medico</p>
        </div>

        {/* 2FA Verification */}
        {twoFaRequired ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-sky-500" />
              <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope' }}>
                Verifica 2FA
              </h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {twoFaMethod === 'email'
                ? 'Inserisci il codice a 6 cifre inviato alla tua email.'
                : 'Inserisci il codice dall\'app di autenticazione.'}
            </p>
            {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <Input
                value={twoFaCode}
                onChange={e => setTwoFaCode(e.target.value)}
                placeholder="000000"
                maxLength={6}
                className="font-mono text-center text-2xl tracking-[0.5em] py-4"
                autoFocus
                data-testid="2fa-login-input"
              />
              <Button type="submit" disabled={loading || twoFaCode.length < 6} className="w-full bg-sky-500 hover:bg-sky-600" data-testid="2fa-login-verify-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Verifica
              </Button>
              <button type="button" onClick={() => { setTwoFaRequired(false); setTwoFaCode(''); setError(''); }}
                className="w-full text-sm text-slate-400 hover:text-slate-600 mt-2">
                Torna al login
              </button>
            </form>
          </div>
        ) : (
        <>
        {/* Login Form */}
        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
          <h2 
            className="text-xl font-semibold text-slate-800 mb-6"
            style={{ fontFamily: 'Manrope' }}
          >
            Accedi
          </h2>

          {error && (
            <div 
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@esempio.it"
                required
                className="mt-1.5"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Inserisci la password"
                  required
                  className="pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-sky-500 hover:bg-sky-600 text-white"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Accesso in corso...
                </>
              ) : (
                'Accedi'
              )}
            </Button>

            <button
              type="button"
              onClick={() => setShowReset(true)}
              className="w-full text-sm text-sky-600 hover:text-sky-700 mt-2"
              data-testid="forgot-password-link"
            >
              Password dimenticata?
            </button>
          </form>
        </div>

        {/* Reset Password Modal */}
        {showReset && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-md" data-testid="reset-password-modal">
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Ripristino Password</h3>
              {resetSent ? (
                <div className="space-y-4">
                  <p className="text-sm text-emerald-600">Se l'email esiste nel sistema, riceverai un link per reimpostare la password.</p>
                  <Button onClick={() => { setShowReset(false); setResetSent(false); setResetEmail(''); }} className="w-full">Chiudi</Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">Inserisci la tua email e ti invieremo le istruzioni per reimpostare la password.</p>
                  <Input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="email@esempio.it"
                    data-testid="reset-email-input"
                  />
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => { setShowReset(false); setResetEmail(''); }}
                      className="flex-1"
                    >
                      Annulla
                    </Button>
                    <Button
                      onClick={async () => {
                        setResetLoading(true);
                        try {
                          await fetch(`${API}/api/auth/reset-password`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ email: resetEmail }),
                          });
                          setResetSent(true);
                        } catch { /* silent */ }
                        finally { setResetLoading(false); }
                      }}
                      disabled={!resetEmail || resetLoading}
                      className="flex-1 bg-sky-500 hover:bg-sky-600 text-white"
                      data-testid="reset-submit-btn"
                    >
                      {resetLoading ? 'Invio...' : 'Invia'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        </>
        )}

        <p className="text-center mt-8">
          <a
            href="/register-partner"
            className="text-sky-500 hover:text-sky-600 font-medium text-sm"
            data-testid="register-partner-link"
          >
            Gestisci uno o piu studi medici? Registrati come Partner
          </a>
        </p>
        <p className="text-center mt-4">
          <a 
            href="https://telcavoip.ai" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[11px] text-slate-400 hover:text-sky-500 transition-colors"
            data-testid="login-footer-badge"
          >
            Made with <span className="text-red-400">&hearts;</span> by <span className="font-semibold text-slate-500 hover:text-sky-500">TelcaVoIP.AI</span>
          </a>
        </p>
        <p className="text-center mt-3">
          <a href="/privacy" className="text-xs text-slate-400 hover:text-sky-500 transition-colors" data-testid="login-privacy-link">
            Informativa Privacy e Cookie
          </a>
        </p>
      </div>
    </div>
  );
}
